import {
    E_ALREADY_LOCKED,
    Mutex,
    tryAcquire
} from "async-mutex";
import {
    Scheduler,
    Task,
    TaskQueue
} from "@tenera/upload_workers"

import { Attachment } from "../../../../models";
import AttachmentContentDAO from "../../content/AttachmentContentDAO";
import AttachmentRecordDAO from "../../record/AttachmentRecordDAO";
import { AttachmentUploadManager } from "./AttachmentUploadManager";
import AttachmentUploadRequest from "../request/AttachmentUploadRequest";
import AttachmentUploadRequestError from "../errors/AttachmentUploadRequestError";
import { AttachmentUploadStatus } from "../status/AttachmentUploadStatus";
import AttachmentUploadStatusDAO from "../status/AttachmentUploadStatusDAO";

export class DefaultAttachmentUploadManager implements AttachmentUploadManager {
    private readonly attachmentUploadRequestQueue: TaskQueue<Task<AttachmentUploadRequest, AttachmentUploadStatus>>;
    private readonly attachmentUploadScheduler: Scheduler;
    private readonly attachmentCycleLock = new Mutex();

    private readonly attachmentUploadStatusDAO: AttachmentUploadStatusDAO;
    private readonly synchronizingAttachmentRecordDAO: AttachmentRecordDAO;
    private readonly localAttachmentRecordDAO: AttachmentRecordDAO;
    private readonly temporaryStorageAttachmentContentDAO: AttachmentContentDAO;

    constructor(
        attachmentUploadStatusDAO: AttachmentUploadStatusDAO,
        synchronizingAttachmentRecordDAO: AttachmentRecordDAO,
        localAttachmentRecordDAO: AttachmentRecordDAO,
        temporaryStorageAttachmentContentDAO: AttachmentContentDAO,
        attachmentUploadRequestQueue: TaskQueue<Task<AttachmentUploadRequest, AttachmentUploadStatus>>,
        attachmentUploadScheduler: Scheduler
    ) {
        this.attachmentUploadStatusDAO = attachmentUploadStatusDAO;
        this.synchronizingAttachmentRecordDAO = synchronizingAttachmentRecordDAO;
        this.localAttachmentRecordDAO = localAttachmentRecordDAO;
        this.temporaryStorageAttachmentContentDAO = temporaryStorageAttachmentContentDAO;
        this.attachmentUploadRequestQueue = attachmentUploadRequestQueue;
        this.attachmentUploadScheduler = attachmentUploadScheduler;
    }

    public async queueAttachmentForUpload(
        attachmentUploadRequest: AttachmentUploadRequest
    ): Promise<void> {
        try {
            const attachmentContentKey: string = attachmentUploadRequest.record.key!;
            const attachmentRecord: Attachment = await this.synchronizingAttachmentRecordDAO.create(attachmentUploadRequest.record);
            await this.localAttachmentRecordDAO.create(attachmentRecord);
            await this.temporaryStorageAttachmentContentDAO.saveAttachment(attachmentContentKey, attachmentUploadRequest.content);
            await this.attachmentUploadStatusDAO.setAttachmentUploadStatus(attachmentRecord.id, AttachmentUploadStatus.PENDING_UPLOAD);
            await this.attachmentUploadRequestQueue.addTask(
                {
                    id: attachmentUploadRequest.record.id,
                    request: attachmentUploadRequest,
                    status: AttachmentUploadStatus.PENDING_UPLOAD
                }
            );
        } catch (error) {
            throw new AttachmentUploadRequestError("Unable to add AttachmentUploadRequest to queue.");
        }
    }

    public async initiateUploadCycle(): Promise<void> {
        // TryAcquire will fail fast if mutex is still locked from previous cycle
        await tryAcquire(this.attachmentCycleLock)
            .runExclusive(async () => {
                await this.attachmentUploadScheduler.run();
            })
            .catch(error => {
                // If tryAcquire fails because mutex is already locked, we swallow the exception and continue
                if (error !== E_ALREADY_LOCKED) {
                    throw error;
                }
            });
    }
}